import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import {
    CeActionTable,
    CeActionTableComponent,
    CeActionTablePill,
    CeButtonComponent,
    CeContainerComponent,
    CeEmptyStateComponent,
    CeIconComponent,
    CeLinkButtonComponent,
    CeSpinningLoaderComponent,
} from '@kempower-nx-workspace/shared/kp-design-system';
import { EventLogEntry, EventLogEntryType, EventLogService } from '../../data-access/event-log.service';

type Status = 'loading' | 'success' | 'error';

const EVENT_TYPE_PILL: Record<EventLogEntryType, CeActionTablePill> = {
    configuration: { state: 'default', text: 'Configuration', style: 'primary' },
    firmware: { state: 'default', text: 'Firmware', style: 'secondary' },
    sealable_parameter: { state: 'default', text: 'Sealable parameter', style: 'primary' },
};

@Component({
    selector: 'lib-event-log-result',
    imports: [
        CommonModule,
        RouterLink,
        CeContainerComponent,
        CeActionTableComponent,
        CeButtonComponent,
        CeLinkButtonComponent,
        CeEmptyStateComponent,
        CeIconComponent,
        CeSpinningLoaderComponent,
    ],
    templateUrl: './event-log-result.component.html',
    styleUrl: './event-log-result.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EventLogResultComponent {
    readonly #route = inject(ActivatedRoute);
    readonly #service = inject(EventLogService);

    readonly chargerId = signal(this.#route.snapshot.paramMap.get('chargerId') ?? '');

    readonly status = signal<Status>('loading');
    readonly events = signal<EventLogEntry[]>([]);
    readonly errorMessage = signal<string | null>(null);
    readonly pdfDownloading = signal(false);

    readonly hasResults = computed(() => this.status() === 'success' && this.events().length > 0);
    readonly showEmpty = computed(() => this.status() === 'success' && this.events().length === 0);
    readonly showError = computed(() => this.status() === 'error');
    readonly showLoading = computed(() => this.status() === 'loading');

    readonly tableData = computed<CeActionTable>(() => ({
        headers: ['Counter', 'Date & time', 'Parameter ID(s)', 'New value', 'Event type'],
        rows: this.events().map((event) => ({
            content: {
                Counter: this.#formatCounter(event.counter),
                'Date & time': this.#formatTimestamp(event.timestamp),
                'Parameter ID(s)': event.parameterIds.join(', '),
                'New value': event.newValue,
                'Event type': EVENT_TYPE_PILL[event.eventType],
            },
        })),
    }));

    constructor() {
        void this.#fetch();
    }

    async downloadPdf(): Promise<void> {
        if (this.pdfDownloading()) return;
        this.pdfDownloading.set(true);
        try {
            const blob = await firstValueFrom(this.#service.fetchPdf(this.chargerId()));
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `ctep-event-log-${this.chargerId()}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } finally {
            this.pdfDownloading.set(false);
        }
    }

    async #fetch(): Promise<void> {
        this.status.set('loading');
        this.errorMessage.set(null);
        try {
            const events = await firstValueFrom(this.#service.fetch(this.chargerId()));
            this.events.set(events);
            this.status.set('success');
        } catch (err) {
            this.events.set([]);
            this.errorMessage.set(this.#messageFor(err));
            this.status.set('error');
        }
    }

    #messageFor(err: unknown): string {
        if (err instanceof HttpErrorResponse) {
            if (err.status === 404) return 'No charger matches that identifier.';
            if (err.status === 0) return 'Could not reach the server. Check your connection and try again.';
            if (err.status === 501) return 'Event log retrieval is not yet implemented.';
            if (err.status >= 500) return 'The event log service is unavailable. Try again in a moment.';
        }
        return 'Could not load the event log. Try again.';
    }

    #formatCounter(counter: number): string {
        return counter.toString().padStart(3, '0');
    }

    #formatTimestamp(iso: string): string {
        const d = new Date(iso);
        const date = d.toISOString().slice(0, 10);
        const time = d.toISOString().slice(11, 19);
        return `${date} ${time} UTC`;
    }
}
